/**
 * Turning one photo into a wrap, on the device.
 *
 * Everything here happens before a byte is uploaded, and that is the point: the server never sees
 * the original, only the tiles and one downscaled copy. Three things come out of a picked photo —
 *
 *   full     the whole image, long side capped, JPEG. Shown once every tile is peeled.
 *   tiles    cols × cols crops of that same downscaled image, base64 JPEG, uploaded one by one.
 *   teaser   the image squeezed to twelve pixels wide and blown back up to ninety-six. It carries
 *            the colours and nothing else, which is exactly how much a teaser is allowed to say.
 *
 * The tiles are cut from the DOWNSCALED image rather than the original, so they line up with the
 * full picture the recipient eventually sees. Cutting from the original and downscaling after
 * would leave a grid that is a pixel or two out at every seam.
 */
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';

/** The long side of the stored image. Big enough to look like a photograph on a phone. */
const LONG_SIDE = 1024;
/** JPEG quality for the full image and the tiles. */
const QUALITY = 0.8;
/** The teaser's two steps: down to a smear, back up to something a grid can sit on. */
const TEASER_SMALL = 12;
const TEASER_BACK_UP = 96;

export interface WrapImages extends WrapTeaser {
  full: string;
  tiles: string[];
}

/** What the giver's own preview needs, and the only part of a wrap that is cheap to make twice. */
export interface WrapTeaser {
  teaser: string;
  width: number;
  height: number;
}

/** One rendered image, as the manipulator hands it back. */
type Rendered = Awaited<ReturnType<ReturnType<typeof ImageManipulator.manipulate>['renderAsync']>>;

/** Base64 with no data-URL prefix — what the upload routes take. */
async function toBase64(ref: Rendered, compress = QUALITY) {
  const saved = await ref.saveAsync({ format: SaveFormat.JPEG, compress, base64: true });
  return saved.base64 ?? '';
}

/** The one downscale everything else is cut from — the long side capped, the aspect untouched. */
async function downscale(uri: string): Promise<Rendered> {
  const base = await ImageManipulator.manipulate(uri).renderAsync();
  const landscape = base.width >= base.height;
  return await ImageManipulator.manipulate(uri)
    .resize(landscape ? { width: LONG_SIDE } : { height: LONG_SIDE })
    .renderAsync();
}

/** Down to twelve pixels wide, then back up: the blur is the information loss, not a filter. */
async function smear(scaled: Rendered): Promise<string> {
  const tiny = await ImageManipulator.manipulate(scaled).resize({ width: TEASER_SMALL }).renderAsync();
  const teaserRef = await ImageManipulator.manipulate(tiny).resize({ width: TEASER_BACK_UP }).renderAsync();
  return `data:image/jpeg;base64,${await toBase64(teaserRef, 0.6)}`;
}

/**
 * Just the teaser, for the giver's own preview of what the wrap will look like when it lands.
 *
 * It runs the SAME two steps `prepareWrap` runs, off the same downscale, which is the point: a
 * preview drawn from the picked photo at full resolution with a blur over it would be a promise
 * about how much shows through that the real twelve-pixel smear does not keep. Nothing is uploaded
 * and nothing is cached — the wrap is cut again at send, a second or two of work on one photo, and
 * a stale preview is a worse thing to have than a repeated resize.
 */
export async function prepareTeaser(uri: string): Promise<WrapTeaser> {
  const scaled = await downscale(uri);
  return { teaser: await smear(scaled), width: scaled.width, height: scaled.height };
}

/**
 * Prepares every image a wrap needs.
 *
 * `cols` is the square root of the tile count — 2, 3 or 4 — and the crop grid is computed from
 * the downscaled dimensions, with the last row and column taking the remainder so no strip of the
 * photograph is lost to rounding.
 */
export async function prepareWrap(uri: string, cols: number): Promise<WrapImages> {
  // One downscale, reused for the full image, for every crop and for the teaser.
  const scaled = await downscale(uri);

  const { width, height } = scaled;
  const full = await toBase64(scaled);

  const tileWidth = Math.floor(width / cols);
  const tileHeight = Math.floor(height / cols);
  const tiles: string[] = [];
  for (let row = 0; row < cols; row++) {
    for (let col = 0; col < cols; col++) {
      const originX = col * tileWidth;
      const originY = row * tileHeight;
      const crop = {
        originX,
        originY,
        // The last row and column absorb the rounding remainder rather than dropping it.
        width: col === cols - 1 ? width - originX : tileWidth,
        height: row === cols - 1 ? height - originY : tileHeight,
      };
      const tile = await ImageManipulator.manipulate(scaled).crop(crop).renderAsync();
      tiles.push(await toBase64(tile));
    }
  }

  return { full, tiles, teaser: await smear(scaled), width, height };
}
